import Button from '@/components/UI/Button/Button';
import Header from '@/components/UI/Header/Header';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { RouteService } from '@/support/RouteService';
import { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateUtils } from '@/utils/Date.utils.ts';
import BenchlingSyncStatusAside from '@/components/UI/BenchlingSyncStatusAside/BenchlingSyncStatusAside';
import {
  BenchlingBackfillApiId,
  BenchlingBackfillV1,
  entityTypeToStringMap,
  errorTypeToStringMap,
  eventFilterOptions,
  syncEventFilterOptions,
  statusToStringMap,
  benchlingBackfillCustomFilterProcessor,
  benchlingSyncEventCustomFilterProcessor,
  SYNC_STATUS_FILTER_CONFIG,
} from '@/model/BenchlingBackfill.model.ts';
import { modalAction } from '@/utils/modal';
import { Dropdown, DropdownMenuItem } from '@/components/UI/Dropdown';
import { RiMoreFill, RiAlertFill } from 'react-icons/ri';
import { ApiService } from '@/support/ApiService';
import { errorToast, successToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError.ts';
import UserAvatar from '@/components/UI/UserAvatar/UserAvatar';
import Tooltip from '@/components/UI/Tooltip/Tooltip';
import Link from '@/components/UI/Link';
import { Panel, Tab, Tabs } from '@/components/UI/Tabs';
import { web as webRoutes } from '@/support/route.ts';
import Truncate from '@/components/UI/Truncate/Truncate.tsx';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { components } from '@/generated/internal-api/openapi-types';
import { _isNil, _notNil } from '@/littledash.ts';
import { featuresSelector } from '@/support/Selectors';
import SyncingActive from '@/components/UI/SyncingActive/SyncingActive';
import { createSelector } from '@reduxjs/toolkit';
import {
  SyncEntityContext,
  SyncEventsSyncEntityRequestProps,
} from '@/components/Modals/SyncEntityDetails/SyncEntityDetails.utils';

// Import the new sync event types
type BenchlingSyncEventV1 = components['schemas']['BenchlingSyncEventV1.schema'];
type BenchlingSyncEventApiId = components['schemas']['BenchlingSyncEventApiId.schema'];

// Type definitions for API table additional props
interface SyncEventsMeta {
  error_count?: number;
}

interface BackfillAdditionalProps {
  in_progress?: boolean;
}

interface ErrorCountAsideProps {
  useTableProps: UseTableProps<BenchlingSyncEventV1>['useTableProps'];
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  liveSyncEnabled: features?.benchling_live_sync ?? false,
}));

const BenchlingBackfill: FC = () => {
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const { liveSyncEnabled } = useSelector(featureSelector);

  const [hasQueuedItems, setHasQueuedItems] = useState(false);
  const [syncEventsMeta, setSyncEventsMeta] = useState<SyncEventsMeta | null>(null);

  // Tab query parameter logic
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  // Refactor when benchling_live_sync removed
  const getTabIndex = (search: string) => {
    const tabId = new URLSearchParams(search).get('tab');
    if (tabId === 'activity' && liveSyncEnabled) {
      return 0;
    }
    if (tabId === 'backfills') {
      return liveSyncEnabled ? 1 : 0;
    }
    return 0; // Default to first available tab
  };

  const getTabId = (index: number) => {
    if (liveSyncEnabled) {
      return index === 0 ? 'activity' : 'backfills';
    }
    return 'backfills';
  };

  const [tabState, setTabState] = useState(getTabIndex(search));

  useEffect(() => {
    const newTabState = getTabIndex(search);
    setTabState((prev) => (newTabState !== prev ? newTabState : prev));
  }, [search, liveSyncEnabled]);

  useEffect(() => {
    navigate({ pathname, search: `tab=${getTabId(tabState)}` });
  }, [tabState, pathname, history, liveSyncEnabled]);

  const closeModal = useCallback(() => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  }, [dispatch]);

  const { invoke: retrySyncEvents } = useApiHook({
    endpoint: 'POST /api/v1/sync-events',
    invokeOnInit: false,
  });

  const handleTableDataChange = (_data: Array<BenchlingBackfillV1>, additionalProps?: BackfillAdditionalProps) => {
    const in_progress = additionalProps?.in_progress ?? false;
    setHasQueuedItems(in_progress);
  };

  const handleSyncEventsDataChange = (_data: Array<BenchlingSyncEventV1>, additionalProps?: SyncEventsMeta) => {
    // Capture the error_count from the top-level response (no longer in meta)
    const errorCount = additionalProps?.error_count;
    setSyncEventsMeta({ error_count: errorCount });
  };

  const cancelBackfill = async (backfillApiId: BenchlingBackfillApiId, onSuccess: () => void) => {
    try {
      const response = await ApiService.call({
        endpoint: 'POST /api/v1/backfill/{backfillApiId}/cancel',
        path: { backfillApiId },
      });

      if (response.type === 'success') {
        successToast('Backfill cancelled successfully');
        onSuccess();
      }
    } catch (error) {
      errorToast('Could not cancel backfill');
      ExceptionHandler.captureException(
        new InVivoError('Could not cancel backfill', {
          cause: error,
          slug: 'backfill-cancel',
        })
      );
    }
  };

  const handleCancelClick = useCallback(
    (backfill: BenchlingBackfillV1, onSuccess: () => void) => {
      openModal('CONFIRM_CANCEL_BACKFILL', {
        createdAt: DateUtils.renderDateTime(backfill.created_at),
        onClick: async () => {
          await cancelBackfill(backfill.api_id, onSuccess);
        },
      });
    },
    [openModal]
  );

  const handleRetrySyncEvents = async (
    eventApiIds: Array<BenchlingSyncEventApiId>,
    refetchTableData: () => Promise<void>
  ) => {
    try {
      await retrySyncEvents({
        body: { events: eventApiIds },
      });
      await refetchTableData();
      successToast(`Retrying ${eventApiIds.length} sync event${eventApiIds.length > 1 ? 's' : ''}`);
    } catch (error) {
      // Error handled by `retrySyncEvents` hook, but still refresh table to show current state
      await refetchTableData();
    }
  };

  // Create the error count AsideComponent for the Activity tab
  const ErrorCountAsideComponent: FC<ErrorCountAsideProps> = ({ useTableProps }) => {
    const errorCount = syncEventsMeta?.error_count ?? 0;

    return (
      <BenchlingSyncStatusAside
        errorCount={errorCount}
        filterConfig={SYNC_STATUS_FILTER_CONFIG}
        useTableProps={useTableProps}
      />
    );
  };

  // Use the original custom filter processor for sync events
  const customSyncEventFilterProcessor = benchlingSyncEventCustomFilterProcessor;

  // Backfills table columns
  const backfillTableColumns: Array<TanStackColumnDef<BenchlingBackfillV1>> = useMemo(
    () => [
      {
        id: 'created_at',
        header: 'Created at',
        accessorKey: 'created_at',
        enableSorting: true,
        isVisible: true,
        size: 160,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => {
          return (
            <Link
              className="link w-100"
              to={
                RouteService.web({
                  route: 'team.studies.benchling_sync.events',
                  path: { benchlingBackfillApiId: row.original.api_id },
                }).route
              }
            >
              {DateUtils.renderDateTime(row.original.created_at, { defaultResponse: '-' })}
            </Link>
          );
        },
      },
      {
        id: 'created_by',
        header: 'Created by',
        accessorKey: 'created_by',
        enableSorting: false,
        isVisible: true,
        size: 105,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => (
          <UserAvatar user={row.original.created_by} tooltip={row.original.created_by.name} />
        ),
      },
      {
        id: 'date_from',
        header: 'From date',
        accessorKey: 'date_from',
        enableSorting: false,
        isVisible: true,
        size: 120,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => (
          <>{DateUtils.renderDate(row.original.date_from, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'date_to',
        header: 'To date',
        accessorKey: 'date_to',
        enableSorting: false,
        isVisible: true,
        size: 120,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => (
          <>{DateUtils.renderDate(row.original.date_to, { defaultResponse: '-' })}</>
        ),
      },
      {
        id: 'queued',
        header: 'In progress',
        accessorKey: 'queued_count',
        enableSorting: false,
        isVisible: true,
        size: 110,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => <>{row.original.queued_count ?? '-'}</>,
      },
      {
        id: 'completed',
        header: 'Completed',
        accessorKey: 'completed_count',
        enableSorting: false,
        isVisible: true,
        size: 110,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => <>{row.original.completed_count ?? '-'}</>,
      },
      {
        id: 'issues',
        header: 'Failed / Cancelled / Deactivated',
        accessorKey: 'issue_count',
        enableSorting: false,
        isVisible: true,
        size: 255,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) => <>{row.original.issue_count ?? '-'}</>,
      },
      {
        id: 'cancelled_by',
        header: 'Cancelled by',
        accessorKey: 'cancelled_by',
        enableSorting: false,
        isVisible: true,
        size: 125,
        cell: ({ row }: { row: { original: BenchlingBackfillV1 } }) =>
          _notNil(row.original.cancelled_by) ? (
            <UserAvatar user={row.original.cancelled_by} tooltip={row.original.cancelled_by.name} />
          ) : (
            '-'
          ),
      },
      {
        id: 'actions',
        enableSorting: false,
        className: 'ui__table--overflow-cell',
        isVisible: true,
        cell: ({
          row,
          table: {
            options: { meta },
          },
        }: {
          row: { original: BenchlingBackfillV1 };
          table: { options: { meta?: { apiTable?: { fetchTableData: () => void } } } };
        }) => {
          const backfill = row.original;
          const fetchTableData = meta?.apiTable?.fetchTableData;

          return (backfill?.queued_count ?? 0) > 0 && _notNil(fetchTableData) ? (
            <span className="cellActions" style={{ textAlign: 'right' }}>
              <Dropdown
                renderMenu={() => (
                  <>
                    <DropdownMenuItem onClick={() => handleCancelClick(backfill, fetchTableData)}>
                      Cancel Backfill
                    </DropdownMenuItem>
                  </>
                )}
              >
                <RiMoreFill size={22} />
              </Dropdown>
            </span>
          ) : null;
        },
      },
    ],
    [handleCancelClick]
  );

  // Activity sync events table columns
  const activityTableColumns: Array<TanStackColumnDef<BenchlingSyncEventV1>> = useMemo(
    () => [
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        enableSorting: true,
        isVisible: true,
        size: 120,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) => (
          <div className="flex items-center justify-between w-100">
            {statusToStringMap[row.original.status]}
            {row.original.status === 'failed' || row.original.status === 'deactivated' ? (
              <RiAlertFill className="red" />
            ) : null}
          </div>
        ),
      },
      {
        id: 'updated_at',
        header: 'Updated at',
        accessorKey: 'updated_at',
        enableSorting: true,
        isVisible: true,
        size: 160,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) =>
          DateUtils.renderDateTime(row.original.updated_at, { defaultResponse: '-' }),
      },
      {
        id: 'study',
        header: 'Study',
        accessorKey: 'study',
        enableSorting: true,
        isVisible: true,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) => {
          if (_isNil(row.original.study)) {
            return '-';
          }

          return row.original.study.deleted ? (
            <Truncate
              tooltip={`${row.original.study.name} (deleted)`}
            >{`${row.original.study.name} (deleted)`}</Truncate>
          ) : (
            <Link
              className="link w-100"
              to={webRoutes('studies.show', {
                id: row.original.study.id,
              })}
            >
              <Truncate tooltip={row.original.study.name}>{row.original.study.name}</Truncate>
            </Link>
          );
        },
      },
      {
        id: 'entity_type',
        header: 'Item type',
        accessorKey: 'entity_type',
        enableSorting: true,
        isVisible: true,
        size: 100,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) =>
          _notNil(entityTypeToStringMap[row.original.entity_type]) ? (
            <div
              className="link"
              onClick={() =>
                openModal('SYNC_ENTITY_DETAILS', {
                  syncEntityRequestProps: {
                    context: SyncEntityContext.SYNC_EVENTS,
                    syncEventApiId: row.original.api_id,
                  } as SyncEventsSyncEntityRequestProps,
                  entityType: row.original.entity_type,
                })
              }
            >
              {entityTypeToStringMap[row.original.entity_type]}
            </div>
          ) : (
            '-'
          ),
      },
      {
        id: 'entity_api_id',
        header: 'Item ID',
        accessorKey: 'entity_api_id',
        enableSorting: false,
        isVisible: false,
        size: 120,
      },
      {
        id: 'event_type',
        header: 'Event type',
        accessorKey: 'event',
        enableSorting: true,
        isVisible: true,
        size: 100,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) => {
          const actionMatch = row.original.event?.match(/[A-Z][a-z]+$/);
          const action = _notNil(actionMatch) ? actionMatch[0] : row.original.event;
          return action ?? '-';
        },
      },
      {
        id: 'error_message',
        header: 'Error message',
        enableSorting: false,
        isVisible: true,
        cell: ({ row }: { row: { original: BenchlingSyncEventV1 } }) => (
          <>
            {_notNil(row.original.error) ? (
              <button
                className="link-button"
                onClick={() => openModal('VIEW_EVENT_ERRORS', { error: row.original.error, closeModal })}
                data-testid="view-errors-button"
              >
                {_notNil(row.original.error_type) ? errorTypeToStringMap[row.original.error_type] : 'View'}
              </button>
            ) : (
              '-'
            )}
          </>
        ),
      },
    ],
    [openModal, closeModal]
  );

  // Bulk actions for activity sync events
  const activityBulkActions: (props: UseTableProps<BenchlingSyncEventV1>) => Array<unknown> = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }) => {
    const eventApiIds = selectedFlatRows.map(({ original: { api_id } }) => api_id);
    const isDisabled: boolean = selectedFlatRows.some(
      ({ original }) =>
        original.status !== 'failed' && original.status !== 'cancelled' && original.status !== 'deactivated'
    );
    return [
      {
        name: `Retry sync event${selectedFlatRows?.length > 1 ? 's' : ''}`,
        disabled: isDisabled,
        tooltip: 'Only failed/cancelled events can be retried',
        action: () =>
          openModal('CONFIRM_RETRY_SYNC_EVENTS', {
            onClick: () => handleRetrySyncEvents(eventApiIds, refetchTableData),
          }),
      },
    ];
  };

  // Use different filter processors for different tabs
  const backfillCustomFilterProcessor = benchlingBackfillCustomFilterProcessor;

  return (
    <div data-test-component="BenchlingBackfill" data-test-element="container">
      <div data-test-element="header">
        <Header mainHeaderText="Benchling Sync" />
      </div>

      <div className="mv3" data-test-element="content">
        <Tabs outerState={[tabState, setTabState]}>
          <div className="mb4 bb b--moon-gray" data-test-element="tab-navigation">
            {liveSyncEnabled && (
              <Tab className="f4" testId="activity-tab" data-test-element="activity-tab">
                Activity
              </Tab>
            )}
            <Tab className="f4" testId="backfills-tab" data-test-element="backfills-tab">
              Backfills
            </Tab>
          </div>

          <div data-test-element="tab-panels">
            {liveSyncEnabled && (
              <Panel>
                <div data-test-element="activity-panel">
                  <APITable
                    apiInfo={{
                      type: 'internalApi',
                      route: RouteService.api({
                        endpoint: 'GET /api/v1/sync-events',
                        path: undefined,
                      }).url,
                    }}
                    columns={activityTableColumns}
                    bulkActions={activityBulkActions}
                    filterOptions={syncEventFilterOptions}
                    customFilterProcessor={customSyncEventFilterProcessor}
                    defaultSortBy={{ id: 'updated_at', desc: true }}
                    reduxTableName="benchlingSyncEvents"
                    hideSearch
                    testIdPrefix="sync-events"
                    onFetchTableData={handleSyncEventsDataChange}
                    AsideComponent={ErrorCountAsideComponent}
                  />
                </div>
              </Panel>
            )}

            <Panel>
              <div data-test-element="backfills-panel">
                <div data-test-element="description">
                  <p className="f6 pb2">
                    Perform a data backfill to sync historical data between In Vivo and Benchling.
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="dib ml1 link blue"
                      href={RouteService.help('benchling_backfill')}
                      data-testid="help-link"
                    >
                      Read more
                    </a>
                  </p>
                </div>
                <APITable
                  apiInfo={{
                    type: 'internalApi',
                    route: RouteService.api({
                      endpoint: 'GET /api/v1/backfill',
                      path: undefined,
                    }).url,
                  }}
                  columns={backfillTableColumns}
                  filterOptions={eventFilterOptions}
                  customFilterProcessor={backfillCustomFilterProcessor}
                  defaultSortBy={{ id: 'created_at', desc: true }}
                  reduxTableName="benchlingBackfill"
                  hideSearch
                  hideFilter
                  testIdPrefix="backfill"
                  onFetchTableData={handleTableDataChange}
                  AsideComponent={({ useTableProps }) => (
                    <div className="flex items-center justify-between w-100" data-test-element="actions">
                      <Tooltip render={hasQueuedItems ? 'Cannot start a backfill while another is in progress' : null}>
                        <Button
                          onClick={() =>
                            openModal('CREATE_BACKFILL', { onCreateBackfill: useTableProps.apiTable.fetchTableData })
                          }
                          disabled={hasQueuedItems}
                          testId="run-backfill-button"
                          className="fr"
                        >
                          Run backfill
                        </Button>
                      </Tooltip>
                      <SyncingActive />
                    </div>
                  )}
                />
              </div>
            </Panel>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

export default BenchlingBackfill;
